import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

export type PrStatus =
  | 'CHANGES_REQUESTED'
  | 'AWAITING_REVIEW'
  | 'APPROVED'
  | 'DRAFT';

export type PrSource = 'review' | 'authored' | 'team';

export type PullRequest = {
  id: number;
  number: number;
  title: string;
  url: string;
  repo: string;
  status: PrStatus;
  source: PrSource;
  updatedAt: string;
  author: string;
  teamAssigned?: boolean;
};

export type PullRequestsBySection = {
  authored: PullRequest[];
  review: PullRequest[];
  team: PullRequest[];
};

type SearchItem = {
  id: number;
  number: number;
  title: string;
  html_url: string;
  repository_url: string;
  updated_at: string;
  draft?: boolean;
  user?: { login: string };
};

type GitHubReview = {
  state: string;
};

const CACHE_TTL = 5 * 60 * 1000;

type CacheEntry = {
  data: PullRequestsBySection;
  timestamp: number;
};

const cache = new Map<string, CacheEntry>();

function getCached(key: string): PullRequestsBySection | undefined {
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    cache.delete(key);
    return undefined;
  }
  return entry.data;
}

function setCache(key: string, data: PullRequestsBySection) {
  cache.set(key, { data, timestamp: Date.now() });
}

function getGitHubTeamSlugs(ownershipEntityRefs: string[]): string[] {
  const slugs = new Set<string>();
  for (const ref of ownershipEntityRefs) {
    const match = ref.match(/^group:[^/]+\/(.+)$/);
    if (match) slugs.add(match[1]);
  }
  return Array.from(slugs);
}

async function getOwnedRepoSlugs(
  catalogApi: { queryEntities: Function },
  ownershipEntityRefs: string[],
): Promise<string[]> {
  if (!ownershipEntityRefs.length) return [];

  const { items } = await catalogApi.queryEntities({
    filter: {
      kind: 'component',
      'relations.ownedBy': ownershipEntityRefs,
    },
    fields: ['metadata.annotations'],
    limit: 100,
  });

  const slugs = new Set<string>();
  for (const entity of items) {
    const slug = (entity.metadata as any)?.annotations?.[
      'github.com/project-slug'
    ];
    if (slug) {
      slugs.add(slug.replace(/\/$/, ''));
    }
  }
  return Array.from(slugs);
}

export const useMyPullRequests = (githubUsername: string | undefined) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  return useAsync(async (): Promise<PullRequestsBySection> => {
    if (!githubUsername) return { authored: [], review: [], team: [] };

    const cached = getCached(githubUsername);
    if (cached) return cached;

    const proxyBase = await discoveryApi.getBaseUrl('proxy');
    const identity = await identityApi.getBackstageIdentity();
    const teamSlugs = getGitHubTeamSlugs(identity.ownershipEntityRefs);
    const repoSlugs = await getOwnedRepoSlugs(
      catalogApi,
      identity.ownershipEntityRefs,
    );

    const search = async (
      query: string,
      perPage = 10,
    ): Promise<SearchItem[]> => {
      const url = `${proxyBase}/github-api/search/issues?q=${encodeURIComponent(query)}&per_page=${perPage}&sort=updated`;
      const res = await fetchApi.fetch(url);
      if (!res.ok) return [];
      const data = await res.json();
      return data.items ?? [];
    };

    const teamReviewQueries = teamSlugs.map(slug =>
      search(
        `type:pr state:open team-review-requested:meltwater/${slug} org:meltwater`,
      ),
    );

    const repoFilter = repoSlugs.length
      ? repoSlugs.map(s => `repo:${s}`).join(' ')
      : '';

    const [reviewRequested, authored, teamPrs, ...teamReviewResults] =
      await Promise.all([
        search(
          `type:pr state:open review-requested:${githubUsername} org:meltwater`,
        ),
        search(`type:pr state:open author:${githubUsername} org:meltwater`),
        repoFilter
          ? search(
              `type:pr state:open ${repoFilter} -author:${githubUsername}`,
              15,
            )
          : Promise.resolve([]),
        ...teamReviewQueries,
      ]);

    const teamIds = new Set(teamReviewResults.flat().map(r => r.id));
    const allReviewRequested: SearchItem[] = [];
    const dedup = new Set<number>();

    for (const item of [...reviewRequested, ...teamReviewResults.flat()]) {
      if (!dedup.has(item.id)) {
        dedup.add(item.id);
        allReviewRequested.push(item);
      }
    }

    const seen = new Set<number>();
    const reviewPrs: PullRequest[] = [];
    const authoredPrs: PullRequest[] = [];
    const teamPrsList: PullRequest[] = [];

    for (const item of allReviewRequested) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);

      reviewPrs.push({
        id: item.id,
        number: item.number,
        title: item.title,
        url: item.html_url,
        repo: item.repository_url.replace('https://api.github.com/repos/', ''),
        status: item.draft ? 'DRAFT' : 'AWAITING_REVIEW',
        source: 'review',
        updatedAt: item.updated_at,
        author: item.user?.login ?? '',
        teamAssigned: teamIds.has(item.id),
      });
    }

    const unseenAuthored = authored.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });

    const authoredWithStatus = await Promise.all(
      unseenAuthored.map(async (item): Promise<PullRequest> => {
        const repoFullName = item.repository_url.replace(
          'https://api.github.com/repos/',
          '',
        );
        let status: PrStatus = item.draft ? 'DRAFT' : 'AWAITING_REVIEW';

        if (!item.draft) {
          try {
            const reviewsUrl = `${proxyBase}/github-api/repos/${repoFullName}/pulls/${item.number}/reviews`;
            const reviewsRes = await fetchApi.fetch(reviewsUrl);
            if (reviewsRes.ok) {
              const reviews: GitHubReview[] = await reviewsRes.json();
              const hasApproval = reviews.some(r => r.state === 'APPROVED');
              const hasChangesRequested = reviews.some(
                r => r.state === 'CHANGES_REQUESTED',
              );
              if (hasChangesRequested) {
                status = 'CHANGES_REQUESTED';
              } else if (hasApproval) {
                status = 'APPROVED';
              }
            }
          } catch {
            // Fall back to AWAITING_REVIEW if reviews fetch fails
          }
        }

        return {
          id: item.id,
          number: item.number,
          title: item.title,
          url: item.html_url,
          repo: repoFullName,
          status,
          source: 'authored',
          updatedAt: item.updated_at,
          author: item.user?.login ?? '',
        };
      }),
    );

    authoredPrs.push(...authoredWithStatus);

    for (const item of teamPrs) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);

      teamPrsList.push({
        id: item.id,
        number: item.number,
        title: item.title,
        url: item.html_url,
        repo: item.repository_url.replace('https://api.github.com/repos/', ''),
        status: item.draft ? 'DRAFT' : 'AWAITING_REVIEW',
        source: 'team',
        updatedAt: item.updated_at,
        author: item.user?.login ?? '',
      });
    }

    const order: Record<PrStatus, number> = {
      CHANGES_REQUESTED: 0,
      AWAITING_REVIEW: 1,
      APPROVED: 2,
      DRAFT: 3,
    };
    authoredPrs.sort((a, b) => order[a.status] - order[b.status]);

    const result: PullRequestsBySection = {
      authored: authoredPrs,
      review: reviewPrs,
      team: teamPrsList,
    };

    setCache(githubUsername, result);
    return result;
  }, [githubUsername, discoveryApi, fetchApi, catalogApi, identityApi]);
};
